/**
 * SurvivalScoring — pure, no Phaser import (node-testable).
 *
 * Score bonuses and high-score record helpers for Survival mode.
 * Wave-clear bonus grows with N so deeper runs feel exponentially rewarding.
 */

// ── Constants ─────────────────────────────────────────────────────────────────

/** Base score awarded on clearing any wave. */
export const SURVIVAL_WAVE_BONUS_BASE = 500;
/** Additional points per wave number (wave 1 = base, wave 2 = base+growth, etc.). */
export const SURVIVAL_WAVE_BONUS_GROWTH = 100;

// ── Types ─────────────────────────────────────────────────────────────────────

/** Persistent survival high-score entry stored in SaveSlotData. */
export interface SurvivalRecord {
  /** Total score accumulated during the run. */
  score: number;
  /** Last wave reached before KO. */
  wave: number;
  /** Hero used for the record run. */
  heroId: string;
  /** Unix-ms timestamp of the record run. */
  date: number;
}

// ── Pure functions ────────────────────────────────────────────────────────────

/**
 * Score bonus awarded on clearing wave N of survival.
 * Grows by SURVIVAL_WAVE_BONUS_GROWTH for each wave past wave 1.
 */
export function survivalWaveClearBonus(waveN: number): number {
  return SURVIVAL_WAVE_BONUS_BASE + (waveN - 1) * SURVIVAL_WAVE_BONUS_GROWTH;
}

/**
 * True when currentScore beats the saved record (or there is no record yet).
 */
export function isSurvivalNewRecord(
  currentScore: number,
  savedRecord: SurvivalRecord | null | undefined,
): boolean {
  if (!savedRecord) return true;
  return currentScore > savedRecord.score;
}

/**
 * One-line gloat banner for the Title screen, e.g. `SURVIVAL BEST · WAVE 7 · 00042500`.
 * Returns null when there is no record yet (caller hides the line).
 * Pure/standalone — pads the score to 8 digits SoR-style without importing HudData.
 */
export function formatSurvivalBest(record: SurvivalRecord | null | undefined): string | null {
  if (!record) return null;
  const wave = Math.max(1, Math.floor(record.wave));
  const score = Math.max(0, Math.floor(record.score)).toString().padStart(8, '0');
  return `SURVIVAL BEST · WAVE ${wave} · ${score}`;
}
